import {useCopyToClipboard} from '@pinecast/common/hooks/useCopyToClipboard';
import CopyPaste from '@pinecast/common/icons/CopyPaste';
import RightArrowSmall from '@pinecast/common/icons/RightArrowSmall';
import Link from '@pinecast/common/Link';
import TextInput from '@pinecast/common/TextInput';
import type {ElementConfig} from '@pinecast/common/types';
import {useIntl} from '@pinecast/i18n';
import * as React from 'react';

type Props = {
  disableVisit?: boolean;
  value: string;
  suffix?: React.ReactNode;
} & Omit<ElementConfig<typeof TextInput>, 'value' | 'suffix' | 'readOnly'>;

const ReadOnlyLinkInput = ({disableVisit, value, suffix, ...rest}: Props) => {
  const intl = useIntl();
  const ref = React.useRef<HTMLInputElement | null>(null);
  // The end cap has no room for a message, and a message under the field would
  // push down the fields stacked below it, so the copy status is a toast at the
  // icon.
  const [copyAnchor, setCopyAnchor] = React.useState<HTMLElement | null>(null);
  const [copy, copyStatus] = useCopyToClipboard(value, {
    toastAnchor: copyAnchor,
  });
  return (
    <TextInput
      readOnly
      ref={ref}
      value={value}
      suffix={[
        <span ref={setCopyAnchor} style={{display: 'inline-flex'}}>
          <Link
            aria-label={intl.formatMessage({
              description: 'Label for button to copy URL',
              defaultMessage: 'Copy link to clipboard',
            })}
            onClick={() => {
              void copy();
              // Selected, the link is ready to copy by hand if the copy fails.
              ref.current!.select();
            }}
          >
            <CopyPaste size={16} style={{display: 'block'}} />
          </Link>
          {copyStatus}
        </span>,
        !disableVisit && (
          <Link
            aria-label={intl.formatMessage({
              description: 'Label for button to open URL',
              defaultMessage: 'Open link',
            })}
            href={value}
            target="_blank"
          >
            <RightArrowSmall />
          </Link>
        ),
        suffix,
      ]}
      {...rest}
    />
  );
};

export default React.memo(ReadOnlyLinkInput);
